/**
 * OrderLineItemsTable
 *
 * Table component displaying order line items with production details.
 * Features a purple header (ThreadHeads brand) and clean table styling.
 */

import { useTranslation } from 'react-i18next';
import { Archive } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { OrderLineItem } from '../../types';

/**
 * "Archived SKU": the line's resolved SKU is archived in the order's own org
 * (derived at read time from skus.retired_at). Quiet grey (the muted Badge,
 * DESIGN §13.1 history treatment): informational, not a blocker. Glyph +
 * visible label, never hover-only (PRODUCTION_DESIGN §10).
 */
export function ArchivedSkuPill({ className }: { className?: string }) {
  const { t } = useTranslation('production');
  return (
    <Badge variant="muted" size="sm" className={cn('shrink-0 whitespace-nowrap', className)}>
      <Archive className="h-3 w-3" aria-hidden="true" />
      {t('orders.archivedSku.label')}
    </Badge>
  );
}

interface OrderLineItemsTableProps {
  /** Line items to display */
  lineItems: OrderLineItem[];
  /** Whether to show cost column (optional, defaults to false) */
  showCost?: boolean;
}

export function OrderLineItemsTable({
  lineItems,
  showCost = false,
}: OrderLineItemsTableProps) {
  return (
    <Card className="overflow-hidden border-primary/20">
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-primary/5">
              <th className="text-left font-medium text-primary px-4 py-2.5 uppercase text-xs tracking-wide">
                Product
              </th>
              <th className="text-center font-medium text-primary px-4 py-2.5 uppercase text-xs tracking-wide w-16">
                Qty
              </th>
              <th className="text-center font-medium text-primary px-4 py-2.5 uppercase text-xs tracking-wide w-20">
                Type
              </th>
              {showCost && (
                <th className="text-right font-medium text-primary px-4 py-2.5 uppercase text-xs tracking-wide w-24">
                  Cost
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y">
            {lineItems.map((item) => {
              // Strip variant suffix from product name if it's redundant with variant_title
              // e.g. "Custom Bootleg Tee - Black / M" with variant_title "Black / M" → show "Custom Bootleg Tee"
              const displayName = item.variant_title && item.product_name.endsWith(` - ${item.variant_title}`)
                ? item.product_name.slice(0, -(` - ${item.variant_title}`).length)
                : item.product_name;

              return (
              <tr key={item.id} className="hover:bg-muted/20 transition-colors">
                {/* Product */}
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-0.5">
                    <span className="font-medium text-foreground">
                      {displayName}
                    </span>
                    {item.variant_title && (
                      <span className="text-xs text-muted-foreground">
                        {item.variant_title}
                      </span>
                    )}
                    {item.sku_archived === true && <ArchivedSkuPill className="mt-0.5 self-start" />}
                  </div>
                </td>

                {/* Quantity */}
                <td className="px-4 py-3 text-center tabular-nums font-medium">
                  {item.quantity}
                </td>

                {/* Type (POD/P&P) */}
                <td className="px-4 py-3 text-center">
                  {item.production_type ? (
                    <Badge
                      variant={item.production_type === 'POD' ? 'default' : 'secondary'}
                      size="sm"
                    >
                      {item.production_type === 'PAP' ? 'P&P' : item.production_type}
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </td>

                {/* Cost (optional) */}
                {showCost && (
                  <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">
                    -
                  </td>
                )}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
